import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const Navbar = ({ cartCount, onOpenCart }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Shop Coffee', href: '#shop-coffee' },
    { name: 'Subscriptions', href: '#subscriptions' },
    { name: 'Equipment', href: '#equipment' },
    { name: 'Roaster\'s Log', href: '#blog' },
  ];

  return (
    <nav className={`fixed w-full z-50 transition-all duration-500 ${
      isScrolled || mobileMenuOpen
        ? 'bg-espresso/95 backdrop-blur-md text-cream py-3 shadow-2xl' 
        : 'bg-transparent text-espresso py-6 md:py-8'
    }`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex justify-between items-center">
        <button 
          className="md:hidden p-2 -ml-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-moss rounded-sm" 
          onClick={() => setMobileMenuOpen(true)}
          aria-label="Open navigation menu"
          aria-expanded={mobileMenuOpen}
        >
          <SafeIcon name="Menu" className="text-2xl" />
        </button>

        <div className="flex-1 md:flex-none flex justify-center md:justify-start">
          <a href="#" aria-label="BrewAtlas Home" className="font-serif text-2xl md:text-3xl font-bold tracking-tighter uppercase flex items-center gap-2 group focus:outline-none focus-visible:ring-2 focus-visible:ring-moss rounded-sm p-1">
            <SafeIcon name="Compass" className={`transition-transform duration-500 group-hover:rotate-180 ${isScrolled ? 'text-moss-light' : 'text-moss'}`} />
            <span className="hidden sm:inline">BrewAtlas</span>
          </a>
        </div>

        <div className="hidden md:flex gap-10 text-[11px] font-bold tracking-[0.2em] uppercase">
          {navLinks.map((link) => (
            <a 
              key={link.name} 
              href={link.href} 
              className="hover:text-moss-light transition-colors relative group focus:outline-none focus-visible:ring-2 focus-visible:ring-moss rounded-sm p-1"
            >
              {link.name}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-moss-light transition-all duration-300 group-hover:w-full"></span>
            </a>
          ))}
        </div>

        <div className="flex-1 md:flex-none flex justify-end items-center gap-4 md:gap-6">
          <button aria-label="Search" className="hidden sm:block hover:text-moss-light transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-moss rounded-sm p-1">
            <SafeIcon name="Search" className="text-xl" />
          </button>
          <button aria-label="User Account" className="hover:text-moss-light transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-moss rounded-sm p-1">
            <SafeIcon name="User" className="text-xl" />
          </button>
          <button 
            aria-label={`Shopping Cart with ${cartCount} items`}
            onClick={onOpenCart}
            className="hover:text-moss-light transition-colors relative p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-moss rounded-sm"
          >
            <SafeIcon name="ShoppingCart" className="text-xl" />
            <AnimatePresence>
              {cartCount > 0 && (
                <motion.span 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  className="absolute -top-1 -right-1 bg-moss text-cream text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-bold"
                >
                  {cartCount}
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, x: '-100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '-100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-0 bg-espresso text-cream z-[60] p-8 flex flex-col h-screen"
            role="dialog"
            aria-modal="true"
          >
            <div className="flex justify-between items-center mb-16">
              <span className="font-serif text-3xl font-bold tracking-tighter">BREWATLAS</span>
              <button 
                onClick={() => setMobileMenuOpen(false)} 
                className="p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-moss rounded-sm"
                aria-label="Close navigation menu"
              >
                <SafeIcon name="X" className="text-3xl" />
              </button>
            </div>
            <div className="flex flex-col gap-8 text-3xl font-serif overflow-y-auto">
              {navLinks.map((link) => (
                <a 
                  key={link.name} 
                  href={link.href} 
                  onClick={() => setMobileMenuOpen(false)} 
                  className="border-b border-white/10 pb-4 flex justify-between items-center group focus:outline-none focus-visible:ring-2 focus-visible:ring-moss"
                >
                  {link.name}
                  <SafeIcon name="ArrowRight" className="text-sm opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
              ))}
            </div>
            <div className="mt-auto pt-8 flex gap-6 text-cream-dark">
              <a href="#" aria-label="Instagram" className="focus:outline-none focus-visible:ring-2 focus-visible:ring-moss rounded-sm"><SafeIcon name="Instagram" className="text-2xl" /></a>
              <a href="#" aria-label="Twitter" className="focus:outline-none focus-visible:ring-2 focus-visible:ring-moss rounded-sm"><SafeIcon name="Twitter" className="text-2xl" /></a>
              <a href="#" aria-label="Facebook" className="focus:outline-none focus-visible:ring-2 focus-visible:ring-moss rounded-sm"><SafeIcon name="Facebook" className="text-2xl" /></a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;